import { useState, useEffect } from 'react';
import './Downloads.css';

const S3_BASE_URL = 'https://www.aviotrack.com/downloads';
const MANIFEST_URL = `${S3_BASE_URL}/manifest.json`;

const FALLBACK_DOWNLOADS = [
  { id: 'mac', name: 'Aviotrack Receiver for Mac', description: 'macOS application for receiving and forwarding ADS-B data to Aviotrack', version: 'unknown', platform: 'macOS', requirements: 'macOS 11 (Big Sur) or later', fileSize: '-', filename: '', icon: '/icons/apple.svg', fileType: 'DMG' },
  { id: 'windows', name: 'Aviotrack Receiver for Windows', description: 'Windows application for receiving and forwarding ADS-B data to Aviotrack', version: 'unknown', platform: 'Windows', requirements: 'Windows 10 or later, 64-bit', fileSize: '-', filename: '', icon: '/icons/windows.svg', fileType: 'EXE' },
  { id: 'linux', name: 'Aviotrack Receiver for Linux / Raspberry Pi', description: 'Linux application for receiving and forwarding ADS-B data to Aviotrack. Ideal for Raspberry Pi headless deployments.', version: 'unknown', platform: 'Linux / Raspberry Pi', requirements: 'Java 21+, x86_64 or ARM64', fileSize: '-', filename: '', icon: '/icons/linux.svg', fileType: 'TAR.GZ' },
];

const SUPPORTED_DEVICES = [
  { name: 'RTL-SDR', type: 'SDR', desc: 'RTL2832U-based USB dongles (most common ADS-B receiver)' },
  { name: 'Airspy Mini / R2', type: 'SDR', desc: 'High-performance SDR receivers with better sensitivity' },
  { name: 'GNS Hulc', type: 'Serial', desc: 'Dedicated ADS-B receiver with Beast protocol output' },
  { name: 'Mode-S Beast', type: 'Serial', desc: 'Classic Mode-S Beast receiver via serial/USB' },
  { name: 'ADSBee 1090', type: 'Serial', desc: 'RP2040-based ADS-B receiver' },
  { name: 'Beast-In (TCP)', type: 'Network', desc: 'Connect to existing decoders (readsb, dump1090) via TCP' },
];

function Downloads({ initialTab = null, onInitialTabConsumed = null }) {
  const [downloads, setDownloads] = useState(FALLBACK_DOWNLOADS);
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(initialTab || 'overview');
  // Deep link from a "Requires Aviotrack Receiver software" banner: land on
  // the requested install tab, then release it so the nav opens Overview next time.
  useEffect(() => {
    if (!initialTab) return;
    setActiveTab(initialTab);
    if (onInitialTabConsumed) onInitialTabConsumed();
  }, [initialTab, onInitialTabConsumed]);

  useEffect(() => {
    fetch(MANIFEST_URL + '?t=' + Date.now())
      .then(res => {
        if (!res.ok) throw new Error(`Failed to fetch manifest: ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (data.downloads && Array.isArray(data.downloads)) {
          const enhanced = data.downloads.map(d => d.id === 'linux' ? {
            ...d,
            name: 'Aviotrack Receiver for Linux / Raspberry Pi',
            platform: 'Linux / Raspberry Pi',
            description: 'Linux application for receiving and forwarding ADS-B data to Aviotrack. Ideal for Raspberry Pi headless deployments.'
          } : d);
          setDownloads(enhanced);
        }
        if (Array.isArray(data.tools)) setTools(data.tools);
        setLoading(false);
      })
      .catch(err => {
        console.error('Failed to load downloads manifest:', err);
        setError('Could not load latest version info');
        setLoading(false);
      });
  }, []);

  const handleDownload = (download) => {
    if (download.filename) {
      window.open(`${S3_BASE_URL}/${download.filename}`, '_blank');
    }
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'install-mac', label: 'macOS Setup' },
    { id: 'install-windows', label: 'Windows Setup' },
    { id: 'install-linux', label: 'Linux / Raspberry Pi' },
    { id: 'install-pi-kit', label: 'Remote Pi Setup' },
    { id: 'config', label: 'Configuration' },
    { id: 'devices', label: 'Supported Devices' },
    { id: 'release-notes', label: 'Release Notes' },
  ];

  return (
    <div className="downloads-page">
      <div className="downloads-container">
        <div className="downloads-header">
          <h1 className="downloads-title">Aviotrack Receiver</h1>
          <p className="downloads-subtitle">
            Receive and forward ADS-B and ACARS data to Aviotrack
          </p>
          {error && <p style={{ color: 'var(--warning-color, #ff9800)', fontSize: '13px' }}>{error}</p>}
        </div>

        <div className="downloads-grid">
          {downloads.map(download => (
            <div key={download.id} className="download-card">
              <div className="download-card-header">
                <div className="platform-icon">
                  <img src={download.icon} alt={download.platform} />
                </div>
                <div className="download-info">
                  <h2 className="download-name">{download.name}</h2>
                  <span className="download-version">
                    {loading ? 'Loading...' : `Version ${download.version}`}
                  </span>
                </div>
              </div>
              <p className="download-description">{download.description}</p>
              <div className="download-details">
                <div className="detail-row">
                  <span className="detail-label">Platform</span>
                  <span className="detail-value">{download.platform}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Requirements</span>
                  <span className="detail-value">{download.requirements}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">File Size</span>
                  <span className="detail-value">{download.fileSize}</span>
                </div>
              </div>
              <button className="btn-download" onClick={() => handleDownload(download)} disabled={!download.filename}>
                <span className="download-icon">&#11015;</span>
                Download {download.fileType}
              </button>
            </div>
          ))}
        </div>

        {/* Tabbed documentation section */}
        <div className="downloads-docs">
          <div className="docs-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                className={`docs-tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="docs-content">
            {activeTab === 'overview' && <OverviewTab />}
            {activeTab === 'install-mac' && <MacSetupTab />}
            {activeTab === 'install-windows' && <WindowsSetupTab />}
            {activeTab === 'install-linux' && <LinuxSetupTab />}
            {activeTab === 'install-pi-kit' && <RemotePiSetupTab tools={tools} onDownload={handleDownload} />}
            {activeTab === 'config' && <ConfigTab />}
            {activeTab === 'devices' && <DevicesTab />}
            {activeTab === 'release-notes' && <ReleaseNotesTab />}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Release history for the receiver.
 *
 * Driven by downloads/release-notes/index.json rather than the .md files beside it: the
 * markdown remains the canonical long-form record, but rendering it here would mean
 * shipping a markdown parser for one page. The index carries the structured fields this
 * view needs, and links out to the full notes for anyone who wants them.
 *
 * Failure is non-fatal and quiet-ish: the downloads above are the reason people visit this
 * page, so a missing index shows a short message rather than an error state.
 */
function ReleaseNotesTab() {
  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`${S3_BASE_URL}/release-notes/index.json`)
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => {
        if (cancelled) return;
        setReleases(Array.isArray(data.releases) ? data.releases : []);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load release notes index:', err);
        setFailed(true);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="doc-section"><p>Loading release notes&hellip;</p></div>;
  if (failed || releases.length === 0) {
    return (
      <div className="doc-section">
        <h3>Release Notes</h3>
        <p>Release notes are not available right now. The version of each download is shown on the cards above.</p>
      </div>
    );
  }

  return (
    <div className="doc-section">
      <h3>Release Notes</h3>
      <p>What changed in each receiver release, newest first.</p>
      {releases.map(r => (
        <div className="release-entry" key={r.version}>
          <div className="release-entry-header">
            <span className="release-version">{r.version}</span>
            {r.date && <span className="release-date">{r.date}</span>}
            {Array.isArray(r.platforms) && r.platforms.map(p => (
              <span className="release-platform" key={p}>{p}</span>
            ))}
          </div>
          {r.summary && <p className="release-summary">{r.summary}</p>}
          {Array.isArray(r.highlights) && r.highlights.length > 0 && (
            <ul className="release-list">
              {r.highlights.map((h, i) => <li key={i}>{h}</li>)}
            </ul>
          )}
          {Array.isArray(r.upgradeNotes) && r.upgradeNotes.length > 0 && (
            <>
              <h4 className="release-subhead">Upgrade notes</h4>
              <ul className="release-list">
                {r.upgradeNotes.map((u, i) => <li key={i}>{u}</li>)}
              </ul>
            </>
          )}
          {r.notes && (
            <a
              className="release-full-link"
              href={`${S3_BASE_URL}/release-notes/${r.notes}`}
              target="_blank"
              rel="noopener noreferrer"
            >Full notes for {r.version} &rarr;</a>
          )}
        </div>
      ))}
    </div>
  );
}

function OverviewTab() {
  return (
    <div className="doc-section">
      <h3>What is the Aviotrack Receiver?</h3>
      <p>
        The Aviotrack Receiver is a cross-platform application that decodes ADS-B and ACARS radio signals
        from aircraft and forwards them to the Aviotrack tracking platform. It supports a wide range of
        USB receiver devices and can run as a GUI application, a terminal display, or a headless service.
      </p>

      <h4>Key Features</h4>
      <ul>
        <li><strong>ADS-B Decoding</strong> &mdash; Receives 1090 MHz ADS-B signals for real-time aircraft tracking (position, altitude, speed, callsign)</li>
        <li><strong>ACARS / VDL2 Support</strong> &mdash; Optional ACARS and VDL Mode 2 message decoding with a second SDR device</li>
        <li><strong>Auto Device Detection</strong> &mdash; Automatically scans USB ports and identifies supported receiver hardware</li>
        <li><strong>Multiple Display Modes</strong> &mdash; GUI window, interactive terminal display (<code>--terminal</code>), or headless service (<code>--headless</code>)</li>
        <li><strong>Coexistence</strong> &mdash; Auto-detects existing decoders (readsb, dump1090) and connects via Beast-In protocol</li>
        <li><strong>Beast Output Server</strong> &mdash; Can serve decoded data to other tools (PiAware, tar1090, Virtual Radar Server)</li>
        <li><strong>Multi-Platform</strong> &mdash; Runs on Windows, macOS, Linux x86_64, and Linux ARM64 (Raspberry Pi)</li>
      </ul>

      <h4>Getting Started</h4>
      <ol>
        <li><strong>Download and install</strong> the receiver for your platform (see tabs above)</li>
        <li><strong>Add a receiver</strong> in Aviotrack: go to your <strong>Receivers</strong> page and click <strong>Add Receiver</strong></li>
        <li><strong>Copy your receiver UUID</strong> from the Aviotrack web app</li>
        <li><strong>Configure the UUID</strong> in the receiver application (platform-specific instructions in the setup tabs)</li>
        <li><strong>Connect your device</strong> &mdash; the receiver will auto-detect it and start feeding data</li>
      </ol>
    </div>
  );
}

function MacSetupTab() {
  return (
    <div className="doc-section">
      <h3>macOS Installation</h3>
      <ol>
        <li>Download and open the DMG file</li>
        <li>Drag "Aviotrack Receiver" to your Applications folder</li>
        <li>Launch it for the first time using the <strong>Opening an Unsigned App</strong> steps below</li>
      </ol>

      <h4>Opening an Unsigned App</h4>
      <p>
        The Aviotrack Receiver is not yet signed with an Apple Developer
        certificate, so on first launch macOS Gatekeeper blocks it with a
        message like <em>&ldquo;Aviotrack Receiver&rdquo; cannot be opened
        because the developer cannot be verified</em>. This is expected.
        Use whichever method matches your macOS version &mdash; you only
        need to do this once, after which the app opens normally.
      </p>
      <ul>
        <li>
          <strong>macOS 14 (Sonoma) and earlier:</strong> right-click (or
          Control-click) the app in Applications, choose <strong>Open</strong>,
          then click <strong>Open</strong> again in the dialog.
        </li>
        <li>
          <strong>macOS 15 (Sequoia) and later:</strong> double-click the app
          once (it will be blocked), then open <strong>System Settings &rarr;
          Privacy &amp; Security</strong>, scroll to the Security section, and
          click <strong>Open Anyway</strong> next to the Aviotrack Receiver
          message. Confirm with Touch ID or your password.
        </li>
        <li>
          <strong>Terminal (any version):</strong> clear the quarantine flag,
          then open the app normally:
          <code className="instruction-code">xattr -dr com.apple.quarantine "/Applications/Aviotrack Receiver.app"</code>
        </li>
      </ul>

      <h4>Install SDR Drivers</h4>
      <p>If using an RTL-SDR or Airspy device, install the drivers via <a href="https://brew.sh" target="_blank" rel="noopener noreferrer">Homebrew</a>:</p>
      <code className="instruction-code">brew install librtlsdr    # For RTL-SDR devices{'\n'}brew install airspy       # For Airspy devices</code>

      <h4>Configure Your Receiver UUID</h4>
      <ol>
        <li>Log in to <strong>aviotrack.com</strong> and go to <strong>Receivers</strong> &rarr; <strong>Add Receiver</strong></li>
        <li>Copy the UUID assigned to your new receiver</li>
        <li>In the Aviotrack Receiver app, open <strong>Settings</strong> &rarr; <strong>Server</strong></li>
        <li>Paste your UUID and click <strong>Save</strong></li>
      </ol>
      <p>The application will auto-detect your USB device and start feeding data to Aviotrack.</p>

      <h4>Configure From the Web</h4>
      <p>
        Once your receiver is connected, you can manage most settings from
        the <strong>Receivers</strong> page on aviotrack.com without
        touching the device. The Configure tab edits frequencies, gain,
        ACARS / VDL Mode 2, Beast output, and stats reporting interval
        live; changes are pushed to the receiver over the same socket it
        uses to send data.
      </p>

      <h4>Display Mode</h4>
      <p>The macOS version runs as a GUI application with a real-time aircraft table, map view, signal stats, and decoded message log. Live performance graphs (CPU, RAM, message rate, decoder diagnostics) are available on the Receivers page.</p>
    </div>
  );
}

function WindowsSetupTab() {
  return (
    <div className="doc-section">
      <h3>Windows Installation</h3>
      <ol>
        <li>Download and run the EXE installer</li>
        <li>Follow the installation prompts</li>
        <li>Launch Aviotrack Receiver from the Start Menu</li>
      </ol>

      <h4>RTL-SDR Driver Setup</h4>
      <p>
        When you first connect an RTL-SDR device, the app will detect it and prompt you to install
        the correct driver using Zadig. Click <strong>Install Driver</strong> and follow the prompts.
        Airspy devices are detected automatically with no driver installation needed.
      </p>

      <h4>Configure Your Receiver UUID</h4>
      <ol>
        <li>Log in to <strong>aviotrack.com</strong> and go to <strong>Receivers</strong> &rarr; <strong>Add Receiver</strong></li>
        <li>Copy the UUID assigned to your new receiver</li>
        <li>In the Aviotrack Receiver app, open <strong>Settings</strong> &rarr; <strong>Server</strong></li>
        <li>Paste your UUID and click <strong>Save</strong></li>
      </ol>
      <p>The application will auto-detect your USB device and start feeding data to Aviotrack.</p>

      <h4>Configure From the Web</h4>
      <p>
        Once connected, most settings can be managed from the
        <strong> Receivers</strong> page on aviotrack.com — including
        frequencies, gain, ACARS / VDL Mode 2, Beast output, and stats
        reporting interval. Changes are pushed to the receiver live over
        the same socket it uses to send data, so you don't need to RDP
        into the host or restart the app.
      </p>

      <h4>Display Mode</h4>
      <p>The Windows version runs as a GUI application with a real-time aircraft table, signal stats, and decoded message log. Live performance graphs (CPU, RAM, message rate, decoder diagnostics) are available on the Receivers page.</p>
    </div>
  );
}

function LinuxSetupTab() {
  return (
    <div className="doc-section">
      <h3>Linux / Raspberry Pi Installation</h3>
      <p>One-line install for Debian/Ubuntu, Fedora, and Arch Linux (including Raspberry Pi OS):</p>
      <code className="instruction-code">sudo bash -c "$(wget -O - https://aviotrack.com/pi-install.sh)"</code>
      <p>
        This automatically installs Java 21, RTL-SDR drivers, configures udev rules for USB device access,
        and sets up a headless systemd service that starts on boot.
      </p>

      <h4>Supported Raspberry Pi Models</h4>
      <p>
        A <strong>64-bit OS is required</strong> (the receiver runs on Java 21,
        which has no 32-bit ARM build). Raspberry Pi <strong>4</strong> and
        <strong> 5</strong> are recommended; Pi 3 / 3+ / 3A+ and Pi Zero 2 W
        work but are not formally tested. Pi Zero / Zero W / Pi 1 / Pi 2 are
        <strong> not supported</strong> (32-bit ARMv6/v7). See the
        <strong> Remote Pi Setup</strong> tab for the full model-support table.
      </p>

      <h4>Configure Your Receiver UUID</h4>
      <ol>
        <li>Log in to <strong>aviotrack.com</strong> and go to <strong>Receivers</strong> &rarr; <strong>Add Receiver</strong></li>
        <li>Copy the UUID assigned to your new receiver</li>
        <li>Run the setup command on your Pi/Linux device:</li>
      </ol>
      <code className="instruction-code">sudo aviotrack-setup YOUR-UUID-HERE</code>
      <p>This writes the UUID to the config and restarts the service. Your receiver should appear as connected in Aviotrack within seconds.</p>

      <h4>Configure From the Web</h4>
      <p>
        Once your Pi is online, most settings can be managed from the
        <strong> Receivers</strong> page on aviotrack.com — frequencies,
        gain, ACARS / VDL Mode 2, Beast output, JVM heap, stats
        reporting interval. Changes are pushed live over the same socket
        the receiver uses to send data, so SSHing in and editing YAML is
        only needed for advanced cases. Live performance graphs (CPU,
        RAM, message rate, decoder diagnostics) are visible on each
        receiver's page.
      </p>

      <h4>Display Modes</h4>
      <table className="docs-table">
        <thead>
          <tr><th>Mode</th><th>Command</th><th>Description</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Headless (default)</td>
            <td><code>sudo systemctl start aviotrack-receiver</code></td>
            <td>Runs as a background service, logs to journalctl</td>
          </tr>
          <tr>
            <td>Terminal</td>
            <td><code>sudo aviotrack-receiver --terminal</code></td>
            <td>Interactive ANSI display with live aircraft table</td>
          </tr>
          <tr>
            <td>GUI</td>
            <td><code>DISPLAY=:0 aviotrack-receiver</code></td>
            <td>Full GUI (requires display connected to Pi)</td>
          </tr>
        </tbody>
      </table>

      <h4>Useful Commands</h4>
      <code className="instruction-code">{
`sudo systemctl status aviotrack-receiver    # Check service status
sudo systemctl restart aviotrack-receiver   # Restart service
sudo systemctl stop aviotrack-receiver      # Stop service
sudo journalctl -u aviotrack-receiver -f    # View live logs
sudo aviotrack-setup <uuid>                 # Set/change receiver UUID
sudo nano /var/lib/aviotrack/.config/aviotrack/aviotrack-receiver.yaml  # Edit config`
      }</code>

      <h4>Coexistence with Existing Decoders</h4>
      <p>
        If your system is already running <strong>readsb</strong>, <strong>dump1090-fa</strong>, or <strong>dump1090</strong>,
        the Aviotrack Receiver will auto-detect them and connect via Beast-In protocol on port 30005 instead of
        trying to access the SDR device directly. This means it works alongside PiAware, RadarBox, and other feeders.
      </p>

      <h4>Dynamic Memory</h4>
      <p>The launcher automatically detects available system RAM and configures the JVM accordingly:</p>
      <table className="docs-table">
        <thead><tr><th>Device</th><th>RAM</th><th>JVM Heap</th></tr></thead>
        <tbody>
          <tr><td>Raspberry Pi 3 / Zero 2 W</td><td>0.5&ndash;1 GB</td><td>128 MB</td></tr>
          <tr><td>Raspberry Pi 4 (2GB)</td><td>2 GB</td><td>256 MB</td></tr>
          <tr><td>Raspberry Pi 4/5 (4GB+)</td><td>4-8 GB</td><td>512 MB</td></tr>
        </tbody>
      </table>

      <h4>Uninstall</h4>
      <code className="instruction-code">sudo aviotrack-uninstall</code>
    </div>
  );
}

function RemotePiSetupTab({ tools = [], onDownload }) {
  const piTools = tools.filter(t => t.id && t.id.startsWith('pi-tool'));
  const mac = piTools.find(t => t.platform === 'macOS');
  const win = piTools.find(t => t.platform === 'Windows');
  return (
    <div className="doc-section">
      <h3>Remote Raspberry Pi Setup (Pi Setup Assistant)</h3>
      <p>
        The <strong>Aviotrack Pi Setup Assistant</strong> is a desktop app that
        stages a Raspberry Pi SD card so the device self-provisions at its
        destination &mdash; the final Wi-Fi setup is done from a phone, with no
        keyboard or monitor needed on site. Prepare the card on your bench, ship
        the Pi, and on first boot it broadcasts a setup hotspot; whoever installs
        it connects a phone, a captive-portal page collects the site Wi-Fi, and
        the Pi joins that network, registers itself, and starts feeding.
      </p>
      <p>
        It replaces the old <code>bake-pi.sh</code> / <code>prep-sdcard</code>
        command-line scripts (still available under <strong>Advanced</strong>, below).
      </p>

      <h4>1 &mdash; Download the Pi Setup Assistant</h4>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', margin: '12px 0' }}>
        {mac && (
          <button className="btn-download" style={{ width: 'auto' }} onClick={() => onDownload(mac)}>
            <span className="download-icon">&#11015;</span>
            macOS (DMG){mac.version ? ' · v' + mac.version : ''}{mac.fileSize ? ' · ' + mac.fileSize : ''}
          </button>
        )}
        {win && (
          <button className="btn-download" style={{ width: 'auto' }} onClick={() => onDownload(win)}>
            <span className="download-icon">&#11015;</span>
            Windows (EXE){win.version ? ' · v' + win.version : ''}{win.fileSize ? ' · ' + win.fileSize : ''}
          </button>
        )}
        {!mac && !win && (
          <span style={{ fontSize: '13px', opacity: 0.7 }}>Loading download links from the release manifest&hellip;</span>
        )}
      </div>
      <div className="security-callout">
        <div className="callout-title">
          <span className="callout-icon" aria-hidden="true">&#128274;</span>
          Getting past the first-launch security block
        </div>
        <p>
          The Pi Setup Assistant isn't code-signed yet, so on first launch your
          OS blocks it with a &ldquo;developer cannot be verified&rdquo; (macOS)
          or SmartScreen (Windows) warning. This is expected &mdash; approve it
          once using the steps below and it opens normally every time after.
        </p>
        <ul>
          <li>
            <strong>macOS 14 (Sonoma) &amp; earlier:</strong> right-click (or
            Control-click) the app in Applications, choose <strong>Open</strong>,
            then click <strong>Open</strong> again in the dialog.
          </li>
          <li>
            <strong>macOS 15 (Sequoia) &amp; later:</strong> double-click the app
            once (it gets blocked), then open <strong>System Settings &rarr;
            Privacy &amp; Security</strong>, scroll to the Security section, and
            click <strong>Open Anyway</strong> next to the Aviotrack message.
            Confirm with Touch&nbsp;ID or your password.
          </li>
          <li>
            <strong>macOS &mdash; Terminal fallback:</strong> clear the quarantine
            flag, then open normally:
            <code>xattr -dr com.apple.quarantine "/Applications/Aviotrack Pi Setup Assistant.app"</code>
          </li>
          <li>
            <strong>Windows:</strong> on the blue <em>&ldquo;Windows protected
            your PC&rdquo;</em> SmartScreen prompt, click <strong>More info</strong>
            &rarr; <strong>Run anyway</strong>.
          </li>
        </ul>
      </div>

      <h4>2 &mdash; Flash the SD card</h4>
      <p>
        With <a href="https://www.raspberrypi.com/software/" target="_blank" rel="noopener noreferrer">Raspberry Pi Imager</a>,
        choose <strong>Raspberry Pi OS Lite (64-bit)</strong>. In the gear / advanced-options
        panel set a hostname, a username + password, <strong>enable SSH</strong>, and your
        Wi-Fi country (optionally your bench Wi-Fi). Write the card and leave it mounted. The
        Assistant only modifies the boot (FAT32) partition &mdash; the OS itself isn't touched.
      </p>

      <h4>3 &mdash; Prepare the card in the Assistant</h4>
      <ol>
        <li><strong>Target Pi</strong> &mdash; pick the model; the app shows its Wi-Fi capability.</li>
        <li><strong>SD card</strong> &mdash; click <strong>Detect SD cards</strong> and select the flashed card.</li>
        <li>
          <strong>Mode</strong> &mdash; choose how the receiver gets registered:
          <ul>
            <li><strong>Generic</strong> &mdash; the recipient completes onboarding at the destination; their email becomes the receiver's owner.</li>
            <li><strong>Pre-configured</strong> &mdash; you already created the receiver under your own account (paste its UUID); the recipient only enters Wi-Fi and gets viewer access.</li>
            <li><strong>Auto-register</strong> &mdash; sign in with your Aviotrack email (a 6-letter code is emailed to you), and the app mints the UUID + grants the recipient viewer access for you. Best for shipping to non-technical users.</li>
          </ul>
        </li>
        <li><strong>Receiver software</strong> &mdash; leave <em>"Auto-install on first boot"</em> checked.</li>
        <li><strong>Wi-Fi</strong> &mdash; leave <em>"Wipe pre-existing Wi-Fi"</em> checked so the captive-portal hotspot appears on first boot.</li>
        <li>Click <strong>Prepare SD card</strong>, wait for it to finish, then eject the card and ship the Pi.</li>
      </ol>

      <h4>Supported Raspberry Pi models</h4>
      <p>
        A <strong>64-bit Pi OS Lite</strong> image is required. Pi 4 and Pi 5 are the
        recommended hardware.
      </p>
      <table className="docs-table">
        <thead><tr><th>Model</th><th>Support</th><th>Notes</th></tr></thead>
        <tbody>
          <tr>
            <td><strong>Pi 5</strong></td>
            <td>Recommended</td>
            <td>First-class. Fastest decoder loop, cleanest Wi-Fi driver.</td>
          </tr>
          <tr>
            <td><strong>Pi 4</strong></td>
            <td>Recommended</td>
            <td>First-class and tested end-to-end.</td>
          </tr>
          <tr>
            <td><strong>Pi 3 / 3+ / 3A+</strong></td>
            <td>Untested but should work</td>
            <td>Same arm64 binary, same Wi-Fi chipset family. RAM is tighter (1 GB) but fine. Use the 64-bit Pi OS Lite image.</td>
          </tr>
          <tr>
            <td><strong>Pi Zero 2 W</strong></td>
            <td>Untested but should work</td>
            <td>Same chipset as Pi 3, 512 MB RAM &mdash; the receiver's JVM heap just fits. Use the 64-bit Pi OS Lite image.</td>
          </tr>
          <tr>
            <td>Pi Zero / Zero W / Pi 1 / Pi 2</td>
            <td>Not supported</td>
            <td>armv6/v7 only &mdash; there is no Java 21 build for 32-bit ARM, so the receiver can't run. Pi 1 / Pi 2 also have no on-board Wi-Fi.</td>
          </tr>
        </tbody>
      </table>

      <h4>4 &mdash; On-site setup (done by phone)</h4>
      <ol>
        <li>Power on the Pi. After ~30&nbsp;seconds it broadcasts a Wi-Fi network named <code>aviotrack-setup-XXXX</code>.</li>
        <li>On a phone, connect to that network &mdash; a setup page opens automatically.</li>
        <li>
          Enter the requested details (depends on the mode chosen at prep):
          <ul>
            <li><strong>Generic:</strong> owner email + Wi-Fi network + password.</li>
            <li><strong>Pre-configured / Auto-register:</strong> just Wi-Fi network + password.</li>
          </ul>
          The hotspot disappears as the Pi joins the real network.
        </li>
        <li>(Generic only) Open the confirmation link in the owner email and use <strong>"Use my current location"</strong> to pin the receiver. The system is passwordless &mdash; clicking the link logs you in and claims ownership.</li>
        <li>The receiver appears on your <strong>Receivers</strong> page and begins feeding data.</li>
      </ol>

      <h4>Advanced &mdash; command-line scripts</h4>
      <p>
        Power users, and headless Linux benches without the GUI, can use the original bash
        scripts instead of the Assistant:
        {' '}<a href="https://www.aviotrack.com/downloads/bake-pi.sh">bake-pi.sh</a>{' '}+
        {' '}<a href="https://www.aviotrack.com/downloads/prep-for-shipment.sh">prep-for-shipment.sh</a>.
        Boot the Pi onto your bench Wi-Fi, then run <code>bash bake-pi.sh</code> &mdash; it
        walks through the same registration modes over SSH and installs the receiver. On
        Windows these run in <strong>Git Bash</strong>. Re-runnable if a step is interrupted.
      </p>
    </div>
  );
}

function ConfigTab() {
  return (
    <div className="doc-section">
      <h3>Configuration</h3>
      <p>
        On <strong>macOS and Windows</strong>, configuration is done through the GUI Settings dialog.
        On <strong>Linux</strong>, edit the YAML config file directly:
      </p>
      <code className="instruction-code">sudo nano /var/lib/aviotrack/.config/aviotrack/aviotrack-receiver.yaml</code>

      <h4>Key Configuration Options</h4>
      <table className="docs-table">
        <thead><tr><th>Setting</th><th>Default</th><th>Description</th></tr></thead>
        <tbody>
          <tr><td><code>servers[].uuid</code></td><td>(empty)</td><td>Your receiver UUID from aviotrack.com</td></tr>
          <tr><td><code>servers[].enabled</code></td><td>true</td><td>Enable/disable feeding to Aviotrack</td></tr>
          <tr><td><code>latitude / longitude</code></td><td>null</td><td>Receiver location (improves position decoding accuracy)</td></tr>
          <tr><td><code>altitude</code></td><td>null</td><td>Receiver altitude in meters (AMSL)</td></tr>
          <tr><td><code>baudRate</code></td><td>921600</td><td>Serial baud rate for Beast/Hulc devices</td></tr>
          <tr><td><code>aircraftTimeoutSeconds</code></td><td>60</td><td>Time before an aircraft is removed from the list</td></tr>
          <tr><td><code>beastInEnabled</code></td><td>false</td><td>Connect to external decoder via TCP (auto-detected on Linux)</td></tr>
          <tr><td><code>beastInHost / beastInPort</code></td><td>127.0.0.1 / 30005</td><td>External decoder address for Beast-In mode</td></tr>
          <tr><td><code>beastOutputEnabled</code></td><td>false</td><td>Serve Beast data on TCP for other tools (PiAware, tar1090)</td></tr>
          <tr><td><code>beastOutputPort</code></td><td>30005</td><td>Port for Beast output server</td></tr>
          <tr><td><code>acarsEnabled</code></td><td>false</td><td>Enable ACARS/VDL2 decoding (requires second SDR device)</td></tr>
          <tr><td><code>gpsEnabled</code></td><td>false</td><td>Enable GPS dongle for automatic location</td></tr>
        </tbody>
      </table>

      <h4>Example Configuration (Linux)</h4>
      <code className="instruction-code">{
`servers:
- enabled: true
  host: "feeders.aviotrack.com"
  port: 30009
  uuid: "xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
  sendLocation: true
latitude: 38.7223
longitude: -27.2196
altitude: 30
aircraftTimeoutSeconds: 60
beastOutputEnabled: false`
      }</code>
      <p>After editing, restart the service:</p>
      <code className="instruction-code">sudo systemctl restart aviotrack-receiver</code>
    </div>
  );
}

function DevicesTab() {
  return (
    <div className="doc-section">
      <h3>Supported Devices</h3>
      <p>
        The Aviotrack Receiver automatically scans USB ports and identifies supported devices.
        No manual device selection is needed &mdash; just plug in your receiver and the application will detect it.
      </p>

      <table className="docs-table">
        <thead><tr><th>Device</th><th>Type</th><th>Description</th></tr></thead>
        <tbody>
          {SUPPORTED_DEVICES.map(d => (
            <tr key={d.name}>
              <td><strong>{d.name}</strong></td>
              <td>{d.type}</td>
              <td>{d.desc}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4>How Device Detection Works</h4>
      <ol>
        <li><strong>Process Detection (Linux)</strong> &mdash; Checks if an existing decoder (readsb, dump1090) is running. If found, connects via Beast-In TCP instead of using the SDR directly.</li>
        <li><strong>Serial Port Scan</strong> &mdash; Scans USB serial ports for known VID:PID combinations (Hulc, Beast, ADSBee).</li>
        <li><strong>SDR Device Scan</strong> &mdash; If no serial device found, scans for Airspy and RTL-SDR USB devices.</li>
      </ol>
      <p>Priority order: Beast-In &gt; Serial devices &gt; Airspy &gt; RTL-SDR</p>

      <h4>Multi-Device Support (ADS-B + ACARS)</h4>
      <p>
        If you have two SDR devices, the receiver can use one for ADS-B (1090 MHz) and another for
        ACARS or VDL Mode 2. The Airspy is preferred for ADS-B (better sensitivity), while RTL-SDR
        works well for ACARS (strong signals). Enable ACARS in the config and the receiver will
        automatically assign devices to the correct pipelines.
      </p>
    </div>
  );
}

export default Downloads;
